/**
 * BreachTemplate.tsx — BreachScan / leaked-database records.
 * Covers: deepweb (PLUTO / HYDRA entities), combined_id when it returns records.
 *
 * Two things this does that the old popup did not:
 *   1. Secrets (password, hash) are MASKED by default with click-to-reveal, so
 *      the page is safe to screenshot or screen-share.
 *   2. Identity extracted across records is shown once at the top instead of
 *      being repeated inside every record card.
 */
import * as React from 'react'
import { Box, Typography } from '@mui/material'
import { prepareDeepWebResult } from '../vendor/searcherService'
import { cleanScalar } from '../adapters/providers'
import {
  Duo, FieldTable, NoData, RawCard, ResCard, Sech, Stats, Tri, ValueList, ValueRow, Warn, useTokens,
} from '../ui/primitives'
import type { TemplateProps } from '../registry'
import { radii } from '../tokens'

const SECRET_KEYS = /^(password|passwd|pass|hash|pwd|secret)$/i
const HIDDEN_KEYS = /^(type|createdTime|created_time|id|requestId)$/i

type Record_ = { fields: [string, string][]; secrets: [string, string][]; searchedBy?: string; created?: string }

/** Pull records out of whatever shape the provider used. */
function extractRecords(apiData: any): Record_[] {
  const out: Record_[] = []
  const entities: any[] = Array.isArray(apiData?.apiDataEntities) ? apiData.apiDataEntities : []

  entities.forEach((e) => {
    const rows = Array.isArray(e?.data) ? e.data : Array.isArray(e?.data?.data) ? e.data.data : []
    rows.forEach((r: any) => {
      if (!r || typeof r !== 'object') return
      const fields: [string, string][] = []
      const secrets: [string, string][] = []
      let searchedBy: string | undefined
      let created: string | undefined

      Object.entries(r).forEach(([k, v]) => {
        if (v === null || v === undefined || v === '') return
        const value = cleanScalar(v)
        if (!value) return
        if (k.toLowerCase() === 'type') { searchedBy = value; return }
        if (/^created/i.test(k)) { created = value; return }
        if (HIDDEN_KEYS.test(k)) return
        if (SECRET_KEYS.test(k)) secrets.push([label(k), value])
        else fields.push([label(k), value])
      })

      if (fields.length || secrets.length) out.push({ fields, secrets, searchedBy, created })
    })
  })
  return out
}

const label = (k: string) =>
  k.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').replace(/^./, (c) => c.toUpperCase())

/** Collect distinct values for a set of field names across all records. */
function collect(records: Record_[], match: RegExp): ValueRow[] {
  const seen = new Map<string, ValueRow>()
  records.forEach((r) =>
    r.fields.forEach(([k, v]) => {
      if (!match.test(k)) return
      if (!seen.has(v)) seen.set(v, { value: v, sources: ['pluto'] })
    }),
  )
  return Array.from(seen.values())
}

export function BreachTemplate({ apiData }: TemplateProps) {
  const tk = useTokens()
  const records = React.useMemo(() => extractRecords(apiData), [apiData])
  const [revealed, setRevealed] = React.useState<Set<string>>(new Set())

  // prepareDeepWebResult is kept available for teams that already rely on it
  const legacy = React.useMemo(() => {
    try { return prepareDeepWebResult(apiData as any) } catch { return null }
  }, [apiData])

  if (!records.length) {
    return <NoData title="No leaked records found" hint={legacy ? 'The provider answered but returned no rows for this identifier.' : undefined} />
  }

  const people = collect(records, /^(first name|last name|name|full name)$/i)
  const emails = collect(records, /mail/i)
  const others = collect(records, /^(phone|address|vk id|facebook id|telegram id|twitter id|linkedin id|instagram id|profession|gender)$/i)
  const secretCount = records.reduce((a, r) => a + r.secrets.length, 0)
  const uniqueSecrets = new Set(records.flatMap((r) => r.secrets.map(([, v]) => v))).size

  const toggle = (id: string) =>
    setRevealed((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  return (
    <>
      <ResCard title="Key facts">
        <Stats
          items={[
            { label: 'Records', value: records.length, sub: 'across the leak set', tone: 'bad' },
            { label: 'Identities', value: people.length, sub: people[0] ? String(people[0].value) : '—' },
            { label: 'E-mails', value: emails.length },
            { label: 'Secrets', value: secretCount, sub: uniqueSecrets === 1 && secretCount > 1 ? 'same value reused' : 'plain text', tone: secretCount ? 'bad' : undefined },
            { label: 'Other identifiers', value: others.length },
          ]}
        />
      </ResCard>

      <ResCard title="Extracted identity">
        <Tri>
          <div>
            <Sech count={people.length}>Person</Sech>
            {people.length ? <ValueList rows={people} /> : <Typography sx={{ fontSize: 12, color: tk.text3 }}>none</Typography>}
          </div>
          <div>
            <Sech count={emails.length}>E-mails</Sech>
            {emails.length ? <ValueList rows={emails} /> : <Typography sx={{ fontSize: 12, color: tk.text3 }}>none</Typography>}
          </div>
          <div>
            <Sech count={others.length}>Other identifiers</Sech>
            {others.length ? <ValueList rows={others} /> : <Typography sx={{ fontSize: 12, color: tk.text3 }}>none</Typography>}
          </div>
        </Tri>
      </ResCard>

      <ResCard
        title="Leaked records"
        count={records.length}
        tone="bad"
        action={
          secretCount > 0 ? (
            <Box
              component="button"
              onClick={() => setRevealed(revealed.size ? new Set() : new Set(records.flatMap((r, i) => r.secrets.map((_, j) => `${i}-${j}`))))}
              sx={{ border: `1px solid ${tk.border}`, background: 'none', cursor: 'pointer', fontSize: 11.5, fontWeight: 600, color: tk.text2, borderRadius: '8px', px: '10px', py: '5px' }}
            >
              {revealed.size ? 'Hide all secrets' : 'Reveal all secrets'}
            </Box>
          ) : undefined
        }
      >
        {records.map((r, i) => {
          const half = Math.ceil(r.fields.length / 2)
          return (
            <Box key={i} sx={{ border: `1px solid ${tk.border}`, borderRadius: `${radii.md}px`, background: tk.surface2, p: '12px 13px', mb: '9px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', mb: '10px', flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: tk.text }}>Record {i + 1}</Typography>
                {r.searchedBy && (
                  <Box component="span" sx={{ fontSize: 11, fontWeight: 600, px: '8px', py: '2px', borderRadius: '6px', background: tk.surface3, color: tk.text2 }}>
                    searched by {r.searchedBy}
                  </Box>
                )}
                {r.created && <Typography sx={{ ml: 'auto', fontSize: 10.5, color: tk.text3 }}>{r.created}</Typography>}
              </Box>

              <Duo gap={14}>
                <FieldTable rows={r.fields.slice(0, half)} />
                <Box>
                  <FieldTable rows={r.fields.slice(half)} />
                  {r.secrets.map(([k, v], j) => {
                    const id = `${i}-${j}`
                    const on = revealed.has(id)
                    return (
                      <Box key={id} sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '7px 14px', fontSize: 12.5, mt: '7px' }}>
                        <Box sx={{ color: tk.text3 }}>{k}</Box>
                        <Box>
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontFamily: 'ui-monospace, Menlo, monospace', background: tk.surface3, border: `1px solid ${tk.border}`, borderRadius: '7px', px: '8px', py: '3px' }}>
                            <Box component="span" sx={{ letterSpacing: on ? 0 : '2px' }}>{on ? v : '••••••••'}</Box>
                            <Box component="button" onClick={() => toggle(id)} sx={{ border: 0, background: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 700, color: '#2D89F4' }}>
                              {on ? 'hide' : 'reveal'}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              </Duo>
            </Box>
          )
        })}

        {secretCount > 0 && (
          <Warn title="Secrets are masked by default">
            Passwords and hashes stay hidden until revealed, so this page is safe to screenshot or screen-share.
            Consider logging reveals for audit.
          </Warn>
        )}
      </ResCard>

      <RawCard data={apiData} />
    </>
  )
}
